/**
 * Zero-Knowledge Encryption Setup with Mnemonic Backup
 *
 * This component implements a zero-knowledge encryption setup that:
 * 1. Registers a WebAuthn device with PRF (Pseudo-Random Function) support
 * 2. Generates a 24-word BIP39 mnemonic phrase for backup/recovery
 * 3. Derives a Data Encryption Key (DEK) from the mnemonic using PBKDF2
 * 4. Creates a Key Encryption Key (KEK) from WebAuthn PRF for local device storage
 * 5. Generates X25519 identity keys encrypted with the mnemonic-derived DEK
 *
 * Key Features:
 * - Mnemonic-based backup: Users can recover their encryption on other devices
 * - WebAuthn PRF: Provides device-bound key derivation without password storage
 * - Zero-knowledge: Private keys and DEK never leave the device unencrypted
 * - Dual encryption: DEK is wrapped with KEK for local storage, private key encrypted with DEK
 *
 * Security Model:
 * - Mnemonic phrase is the master secret (must be backed up securely)
 * - WebAuthn PRF provides device-specific key wrapping
 * - All encryption happens client-side
 * - Server only stores wrapped/encrypted data
 */
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Card,
  CardContent,
  Divider,
  Paper,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { startRegistration } from '@simplewebauthn/browser';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import * as bip39 from 'bip39';
import ToastMessage from '@/components/toasts/ToastMessage';
import auth from '@/services/AuthService';
import translate from '@/i18n/translate';
import { Done, Lock } from '@mui/icons-material';

function arrayBufferToBase64(buffer) {
  try {
    if (!buffer || !(buffer instanceof ArrayBuffer)) {
      throw new Error('Invalid buffer: expected ArrayBuffer');
    }

    var binary = '';
    var bytes = new Uint8Array(buffer);
    var len = bytes.byteLength;

    if (len === 0) {
      throw new Error('Empty buffer provided');
    }

    for (var i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  } catch (error) {
    throw new Error('Failed to encode buffer to base64: ' + error.message);
  }
}

function base64URLStringToBuffer(base64URLString) {
  const base64 = base64URLString.replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=')), c => c.charCodeAt(0)).buffer;
}

async function deriveDEKFromMnemonic(mnemonic, passphrase = '') {
  try {
    // Validate mnemonic
    if (!bip39.validateMnemonic(mnemonic)) {
      throw new Error('Invalid mnemonic phrase');
    }

    // Generate seed from mnemonic
    const seed = await bip39.mnemonicToSeed(mnemonic, passphrase);

    // Use PBKDF2 to derive a 32-byte key for AES-256-GCM
    const salt = new TextEncoder().encode('codemeta:v1:dek-salt');
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      seed.slice(0, 64), // Use first 64 bytes of seed
      'PBKDF2',
      false,
      ['deriveKey'],
    );

    const dek = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 100000,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      true, // extractable for wrapping
      ['encrypt', 'decrypt', 'wrapKey', 'unwrapKey'],
    );

    return dek;
  } catch (error) {
    throw new Error('Failed to derive DEK from mnemonic: ' + error.message);
  }
}

// Export function for use in other components (e.g., recovery flow)
export async function recoverDEKFromMnemonic(mnemonic, passphrase = '') {
  return await deriveDEKFromMnemonic(mnemonic, passphrase);
}

async function deriveKEK(authenticators, label, chall) {
  // Input validation
  if (
    !authenticators ||
    !Array.isArray(authenticators) ||
    authenticators.length === 0
  ) {
    throw new Error('Invalid authenticators: must be a non-empty array');
  }
  if (!label || typeof label !== 'string') {
    throw new Error('Invalid label: must be a non-empty string');
  }

  const prfLabel = new TextEncoder().encode(label);
  const hkdfSalt = crypto.getRandomValues(new Uint8Array(16)); // store with ciphertext

  // Use secure random challenge with additional entropy
  let challenge;
  if (chall) {
    challenge = base64URLStringToBuffer(chall);
  } else {
    challenge = crypto.getRandomValues(new Uint8Array(32)).buffer;
  }

  const toBytes = (s) => {
    try {
      const padding = '='.repeat((4 - (s.length % 4)) % 4);
      const b64 = (s + padding).replace(/-/g, '+').replace(/_/g, '/');
      const raw = atob(b64);
      return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
    } catch (error) {
      throw new Error('Failed to decode credential ID: ' + error.message);
    }
  };

  let assertion;
  try {
    assertion = await navigator.credentials.get({
      publicKey: {
        challenge,
        allowCredentials: authenticators
          .filter((a) => a.credentialId)
          .map((a) => ({
            type: 'public-key',
            id: toBytes(a.credentialId),
          })),
        userVerification: 'required',
        extensions: {
          prf: {
            eval: { first: prfLabel },
          },
        },
      },
    });
  } catch (error) {
    console.log(error);
    throw new Error('WebAuthn assertion failed: ' + error.message);
  }

  if (!assertion) throw new Error('No assertion received from authenticator');
  if (assertion.type !== 'public-key')
    throw new Error('Invalid assertion type: expected public-key');

  const { prf } = assertion.getClientExtensionResults();
  if (!prf?.results?.first)
    throw new Error('PRF extension not available or failed');

  const prfOutput = new Uint8Array(prf.results.first);

  // Validate PRF output length
  if (prfOutput.length < 32) {
    throw new Error('PRF output too short for secure key derivation');
  }

  try {
    // HKDF to make a KEK (AES-GCM-256)
    const ikm = await crypto.subtle.importKey('raw', prfOutput, 'HKDF', false, [
      'deriveKey',
    ]);

    const kek = await crypto.subtle.deriveKey(
      {
        name: 'HKDF',
        hash: 'SHA-256',
        salt: hkdfSalt,
        info: prfLabel /* domain sep */,
      },
      ikm,
      { name: 'AES-GCM', length: 256 },
      false,
      ['wrapKey', 'unwrapKey'],
    );

    return { kek, hkdfSalt, credentialId: assertion.id, assertion };
  } catch (error) {
    throw new Error('Key derivation failed: ' + error.message);
  }
}

export default function EncryptionForm() {
  const [currentStep, setCurrentStep] = useState(0);
  const [stepLoading, setStepLoading] = useState(false);
  const [mnemonic, setMnemonic] = useState('');
  const [mnemonicConfirmed, setMnemonicConfirmed] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [encryptors, setEncryptors] = useState([]);
  const [challenge, setChallenge] = useState(null);
  let attResp;

  useEffect(() => {
    auth.get('/encryptors').then(({ data: { encryptors, challenge } }) => {
      setEncryptors(encryptors);
      setChallenge(challenge);
      if (encryptors.length === 0) {
        setCurrentStep(1); // No encryptors, start from beginning
      } else {
        setCurrentStep(0); // Encryptors exist, start from authentication
      }
    });
  }, []);


  // Determine initial step based on current state
  async function handleAuthenticate() {
    const { data } = await auth.get('/encryptors');
    const { encryptors, challenge } = data;
    if (encryptors.length > 0 && currentStep < 1) {
      await deriveKEK(encryptors, 'codemeta:v1:kek', challenge)
        .then(async ({ credentialId, assertion, kek }) => {
          setRegistered(credentialId);
          const {data: res} = await auth.post('/encryptors/keys', assertion);
          console.log(res);
          if (!res?.wrappedDEK) {
            setCurrentStep(2); // Device registered, ready for key generation
          } else {
            // Unwrap DEK and load into memory
            const wrappedDEKBuf = base64URLStringToBuffer(res.wrappedDEK);
            const dekIv = base64URLStringToBuffer(res.dekWrapIv);
            try {
              const dekKey = await crypto.subtle.unwrapKey(
                'raw',
                wrappedDEKBuf,
                kek,
                { name: 'AES-GCM', iv: dekIv },
                { name: 'AES-GCM', length: 256 },
                true,
                ['encrypt', 'decrypt', 'wrapKey', 'unwrapKey']
              );
              // Store unwrapped DEK in memory for further use
              window.tempDEK = dekKey;
            } catch (error) {
              console.error('Failed to unwrap DEK:', error);
              toast.error(
                <ToastMessage title="Fehler" subtitle="DEK konnte nicht entschlüsselt werden" />
              );
              return;
            }
            
            setCurrentStep(7); // Keys already exist, skip to end
          }
        })
        .catch((err) => {
          console.error('Failed to derive KEK:', err);
          // TODO!
          if (err.message.includes('??')) {
            setCurrentStep(1); // Device registration needed
          }
        });
    }
  }

  const deleteWebauthn = async () => {
    // TODO: Find out which encryptor to delete?
    // try {
    //   await auth.delete('/encryptors/');
    //   setAuthenticators([]);
    //   setCurrentStep(0); // Reset to device registration step
    //   toast.success(
    //     <ToastMessage
    //       title="Erfolg"
    //       subtitle="WebAuthn erfolgreich gelöscht"
    //     />,
    //   );
    // } catch (e) {
    //   console.error('WebAuthn deletion failed:', e);
    //   toast.error(
    //     <ToastMessage
    //       title="Fehler"
    //       subtitle={e?.response?.data?.message || 'Failed to delete WebAuthn'}
    //     />,
    //   );
    // }
  };

  const handleRegister = async () => {
    setStepLoading(true);
    try {
      const { data: registrationOptions } = await auth.get(
        '/encryptors/register',
      );
      if (!registrationOptions) {
        toast.error(
          <ToastMessage
            title="Fehler"
            subtitle="Registrierungsoptionen konnten nicht abgerufen werden"
          />,
        );
        return;
      }

      // 1 - Register webauthn credentials with PRF
      attResp = await startRegistration({
        optionsJSON: registrationOptions,
      });

      const verificationsResp = await auth.post('/encryptors', attResp, {
        validateStatus: (status) => status < 500,
      });

      if (verificationsResp.status !== 204) {
        toast.error(
          <ToastMessage
            title="Fehler"
            subtitle={
              translate(
                verificationsResp?.data?.message ||
                  'Geräteregistrierung fehlgeschlagen',
              ).text
            }
          />,
        );
        return;
      }

      toast.success(
        <ToastMessage
          title="Erfolg"
          subtitle="Gerät erfolgreich registriert"
        />,
      );

      setCurrentStep(2); // Move to key generation step
    } catch (e) {
      console.error('WebAuthn registration failed:', e);

      // Attempt cleanup of temporary registration options
      try {
        await auth.post('/webauthn/deleteRegistrationOptions');
      } catch (cleanupError) {
        console.error('Failed to cleanup registration options:', cleanupError);
      }

      toast.error(
        <ToastMessage
          title="Fehler"
          subtitle={e?.message || 'Registrierung fehlgeschlagen'}
        />,
      );
    } finally {
      setStepLoading(false);
    }
  };

  const handleStartKeyGeneration = () => {
    setCurrentStep(3); // Move to mnemonic generation step
  };

  const handleGenerateMnemonic = () => {
    try {
      const newMnemonic = bip39.generateMnemonic(256); // 24 words for extra security
      setMnemonic(newMnemonic);
      setCurrentStep(4); // Move to mnemonic backup step
    } catch (error) {
      console.error('Mnemonic generation failed:', error);
      toast.error(
        <ToastMessage
          title="Fehler"
          subtitle="Mnemonic-Phrase konnte nicht generiert werden"
        />,
      );
    }
  };

  const handleConfirmMnemonicBackup = () => {
    if (!mnemonicConfirmed) {
      toast.error(
        <ToastMessage
          title="Fehler"
          subtitle="Bitte bestätigen Sie, dass Sie die Mnemonic-Phrase gesichert haben"
        />,
      );
      return;
    }
    setCurrentStep(5); // Move to key derivation step
  };

  const handleDataKeyDerivation = async () => {
    setStepLoading(true);
    try {
      const { data: authenticationOptions } = await auth.get('/encryptors');

      const encryptors = authenticationOptions.allowCredentials;

      // Derive data key encryption key (KEK) using PRF extension
      const { kek, hkdfSalt, credentialId } = await deriveKEK(
        encryptors,
        'codemeta:v1:kek-data',
      );

      // Derive DEK from mnemonic
      const dek = await deriveDEKFromMnemonic(mnemonic);

      // Store KEK and DEK temporarily for final step
      window.tempDataKEK = { kek, hkdfSalt };
      window.tempDEK = dek;
      window.tempCredentialId = credentialId;
      setCurrentStep(6); // Move to final key generation
    } catch (error) {
      console.error('Data key derivation failed:', error);
      toast.error(
        <ToastMessage
          title="Fehler"
          subtitle="Datenschlüssel-Ableitung fehlgeschlagen"
        />,
      );
    } finally {
      setStepLoading(false);
    }
  };

  const handleFinalKeyGeneration = async () => {
    setStepLoading(true);
    try {
      // Get temporarily stored KEK and DEK
      const { kek, hkdfSalt } = window.tempDataKEK;
      const dek = window.tempDEK;

      // Wrap the mnemonic-derived DEK with KEK for storage
      const dekWrapIv = crypto.getRandomValues(new Uint8Array(12));
      const wrappedDEK = await crypto.subtle.wrapKey('raw', dek, kek, {
        name: 'AES-GCM',
        iv: dekWrapIv,
      });

      // Generate X25519 identity key pair
      const x25519KeyPair = await crypto.subtle.generateKey(
        { name: 'X25519' },
        true,
        ['deriveBits', 'deriveKey'],
      );

      // Wrap private key with mnemonic-derived DEK
      const privateKeyWrapIv = crypto.getRandomValues(new Uint8Array(12));
      const wrappedPrivateKey = await crypto.subtle.wrapKey(
        'jwk',
        x25519KeyPair.privateKey,
        dek,
        {
          name: 'AES-GCM',
          iv: privateKeyWrapIv,
        },
      );

      // Store public key in user profile (base64-encoded)
      const publicKeyJwk = await crypto.subtle.exportKey(
        'jwk',
        x25519KeyPair.publicKey,
      );

      // Validate public key before storing
      if (!publicKeyJwk?.x || typeof publicKeyJwk.x !== 'string') {
        throw new Error('Invalid public key generated');
      }

      await auth.put('/me', {
        publicKey: publicKeyJwk.x,
      });

      // Store wrapped DEK, wrapped private key, HKDF salt and wrap IVs in device record
      console.log(
        wrappedDEK,
        dekWrapIv,
        hkdfSalt,
        wrappedPrivateKey,
        privateKeyWrapIv,
      );
      await auth.put('/encryptors', {
        credentialId: window.tempCredentialId,
        wrappedDEK: arrayBufferToBase64(wrappedDEK), // ArrayBuffer from wrapKey
        dekWrapIv: arrayBufferToBase64(dekWrapIv.buffer), // Convert Uint8Array to ArrayBuffer
        hkdfSalt: arrayBufferToBase64(hkdfSalt.buffer), // ArrayBuffer from deriveKEK
        wrappedPrivateKey: arrayBufferToBase64(wrappedPrivateKey), // ArrayBuffer from wrapKey
        privateKeyWrapIv: arrayBufferToBase64(privateKeyWrapIv.buffer), // Convert Uint8Array to ArrayBuffer
        algo: 'AES-GCM-256',
        version: 1,
        mnemonicDerived: true, // Flag to indicate DEK is derived from mnemonic
      });

      // Cleanup temporary storage
      delete window.tempDataKEK;
      delete window.tempDEK;

      setCurrentStep(7); // Success step

      toast.success(
        <ToastMessage
          title="Erfolg"
          subtitle="Verschlüsselung erfolgreich eingerichtet!"
        />,
      );
    } catch (error) {
      console.error('Final key generation failed:', error);
      toast.error(
        <ToastMessage
          title="Fehler"
          subtitle="Schlüsselgenerierung fehlgeschlagen"
        />,
      );
    } finally {
      setStepLoading(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 4,
        display: 'flex',
        flexFlow: 'column',
        px: 2,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 'bold',
          marginBottom: '20px',
        }}
      >
        Zero-Knowledge Verschlüsselung einrichten
      </Typography>

      <Alert sx={{ mb: 3 }} severity="info">
        Die Zero-Knowledge Verschlüsselung schützt Ihre Daten durch lokale
        Verschlüsselung auf Ihrem Gerät. Ihre Schlüssel werden niemals an unsere
        Server übertragen. Verwenden Sie diese Funktion nur auf
        vertrauenswürdigen, privaten Geräten.
      </Alert>

      <Stepper activeStep={currentStep} orientation="vertical">
        <Step>
          {encryptors.length === 0 ? (
            <StepLabel slots={{ StepIcon: <Done /> }}>Einstellungen freischalten</StepLabel>
          ) : (
            <StepLabel>Einstellungen freischalten</StepLabel>
          )}
          <StepContent sx={{ textAlign: 'left' }}>
            <Button variant="outlined" onClick={handleAuthenticate} startIcon={<Lock />}>
              Authentifizieren
            </Button>
          </StepContent>
        </Step>
        {/* Step 0: Device Registration */}
        <Step>
          <StepLabel>Sicherheitsschlüssel einrichten</StepLabel>
          <StepContent>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography gutterBottom>
                  Zuerst müssen wir Ihr Gerät als Sicherheitsschlüssel
                  registrieren. Dies ermöglicht eine sichere Authentifizierung
                  ohne Passwort.
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Sie werden aufgefordert, Windows Hello, TouchID oder Ihren
                  Hardware-Schlüssel zu verwenden.
                </Typography>
                {stepLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={20} />
                    <Typography>Gerät wird registriert...</Typography>
                  </Box>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleRegister}
                    disabled={registered > 0}
                  >
                    {registered.length > 0
                      ? 'Gerät bereits registriert'
                      : 'Gerät registrieren'}
                  </Button>
                )}
              </CardContent>
            </Card>
          </StepContent>
        </Step>

        {/* Step 1: Start Key Generation */}
        <Step>
          <StepLabel>Verschlüsselung aktivieren</StepLabel>
          <StepContent>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography gutterBottom>
                  Ihr Gerät ist erfolgreich registriert! Jetzt können wir die
                  Verschlüsselungsschlüssel generieren.
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Wir generieren eine Wiederherstellungsphrase (Mnemonic), mit
                  der Sie Ihre Schlüssel auf anderen Geräten wiederherstellen
                  können.
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleStartKeyGeneration}
                  disabled={stepLoading}
                >
                  Verschlüsselungsschlüssel generieren
                </Button>
                <Divider sx={{ my: 2 }} />
                <Button
                  variant="outlined"
                  color="error"
                  onClick={deleteWebauthn}
                  fullWidth
                >
                  Gerät entfernen
                </Button>
              </CardContent>
            </Card>
          </StepContent>
        </Step>

        {/* Step 2: Generate Mnemonic */}
        <Step>
          <StepLabel>Wiederherstellungsphrase generieren (1/4)</StepLabel>
          <StepContent>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography gutterBottom>
                  <strong>Wiederherstellungsphrase generieren</strong>
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Wir generieren eine 24-Wort Wiederherstellungsphrase. Diese
                  wird verwendet, um Ihre Verschlüsselungsschlüssel zu erstellen
                  und kann später zur Wiederherstellung auf anderen Geräten
                  verwendet werden.
                </Typography>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <strong>Wichtig:</strong> Diese Phrase ist der einzige Weg,
                  Ihre Daten wiederherzustellen, falls Sie den Zugriff auf
                  dieses Gerät verlieren!
                </Alert>
                <Button
                  variant="contained"
                  onClick={handleGenerateMnemonic}
                  disabled={stepLoading}
                >
                  Wiederherstellungsphrase generieren
                </Button>
              </CardContent>
            </Card>
          </StepContent>
        </Step>

        {/* Step 3: Backup Mnemonic */}
        <Step>
          <StepLabel>Wiederherstellungsphrase sichern (2/4)</StepLabel>
          <StepContent>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography gutterBottom>
                  <strong>Wiederherstellungsphrase sichern</strong>
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Schreiben Sie diese 24 Wörter in der richtigen Reihenfolge auf
                  und bewahren Sie sie an einem sicheren Ort auf. Ohne diese
                  Phrase können Sie Ihre Daten nicht wiederherstellen!
                </Typography>

                <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                  <strong>Niemals teilen und sicher aufbewahren!</strong>
                  <br />
                  Schreiben Sie die Phrase am besten auf Papier.
                </Alert>

                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    mb: 3,
                    backgroundColor: 'grey.50',
                    border: '2px dashed',
                    borderColor: 'primary.main',
                  }}
                >
                  {mnemonic}
                  {/* <Grid container spacing={1}>
                    {mnemonic.split(' ').map((word, index) => (
                      <Grid item xs={3} key={index}>
                        <Chip 
                          label={`${index + 1}. ${word}`}
                          variant="outlined"
                          size="small"
                          sx={{ width: '100%', justifyContent: 'flex-start' }}
                        />
                      </Grid>
                    ))}
                  </Grid> */}
                </Paper>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={mnemonicConfirmed}
                      onChange={(e) => setMnemonicConfirmed(e.target.checked)}
                    />
                  }
                  label="Ich habe die Wiederherstellungsphrase sicher aufgeschrieben und verstehe, dass sie nicht wiederhergestellt werden kann."
                  sx={{
                    mb: 2,
                    textAlign: 'left',
                    '& .MuiTypography-root': {
                      fontSize: '14px',
                      lineHeight: '15px',
                      color: 'text.secondary',
                    },
                  }}
                />

                <Button
                  variant="contained"
                  onClick={handleConfirmMnemonicBackup}
                  disabled={!mnemonicConfirmed}
                  fullWidth
                >
                  Phrase wurde gesichert - Weiter
                </Button>
              </CardContent>
            </Card>
          </StepContent>
        </Step>

        {/* Step 4: Data Key Derivation */}
        <Step>
          <StepLabel>Datenschlüssel ableiten (3/4)</StepLabel>
          <StepContent>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography gutterBottom>
                  <strong>Erste Authentifizierung:</strong> Datenschlüssel
                  ableiten
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Wir leiten jetzt den Hauptschlüssel für Ihre
                  Datenverschlüsselung ab. Bestätigen Sie die Authentifizierung
                  wenn Sie dazu aufgefordert werden.
                </Typography>
                {stepLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={20} />
                    <Typography>Authentifizierung läuft...</Typography>
                  </Box>
                ) : (
                  <Button variant="contained" onClick={handleDataKeyDerivation}>
                    Datenschlüssel ableiten
                  </Button>
                )
                }
              </CardContent>
            </Card>
          </StepContent>
        </Step>

        {/* Step 5: Final Key Generation */}
        <Step>
          <StepLabel>Schlüssel finalisieren (4/4)</StepLabel>
          <StepContent>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography gutterBottom>
                  <strong>Finale Schritte:</strong> Schlüssel generieren und
                  speichern
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Wir generieren jetzt Ihre Identitätsschlüssel und
                  verschlüsseln sie mit dem aus Ihrer Wiederherstellungsphrase
                  abgeleiteten Schlüssel.
                </Typography>
                {stepLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CircularProgress size={20} />
                    <Typography>Schlüssel werden generiert...</Typography>
                  </Box>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleFinalKeyGeneration}
                  >
                    Schlüsselgenerierung abschließen
                  </Button>
                )}
              </CardContent>
            </Card>
          </StepContent>
        </Step>

        {/* Step 6: Success */}
        <Step>
          <StepLabel>Verschlüsselung aktiv</StepLabel>
          <StepContent>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography gutterBottom color="success.main">
                  🎉 <strong>Erfolgreich eingerichtet!</strong>
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Ihre Zero-Knowledge Verschlüsselung ist jetzt aktiv. Alle Ihre
                  Daten werden lokal verschlüsselt, bevor sie gespeichert
                  werden.
                </Typography>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Ihre Verschlüsselungsschlüssel sind sicher auf Ihrem Gerät
                  gespeichert. Bewahren Sie Ihre Wiederherstellungsphrase sicher
                  auf!
                </Alert>
                <Alert severity="info">
                  <strong>Wiederherstellung:</strong> Mit Ihrer 24-Wort
                  Wiederherstellungsphrase können Sie Ihre Verschlüsselung auf
                  anderen Geräten einrichten und Ihre Daten entschlüsseln.
                </Alert>
              </CardContent>
            </Card>
          </StepContent>
        </Step>
      </Stepper>
    </Box>
  );
}
